import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, CalendarDays, ClipboardList, Settings as SettingsIcon, LogOut, Menu, Sparkles, ExternalLink, Mail, Handshake, Users, ShieldAlert, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { clearAuth, useUser } from '@/lib/auth';
import { useEffect, useState } from 'react';
import api from '@/lib/api';

export default function AdminLayout({ children, title }) {
  const navigate = useNavigate();
  const user = useUser();
  const [pending, setPending] = useState(0);
  const [collabPending, setCollabPending] = useState(0);

  useEffect(() => {
    if (!user && !localStorage.getItem('admin_token')) {
      navigate('/admin/login');
      return;
    }
    api.get('/admin/stats/lite').then((r) => setPending(r.data?.in_revisione || 0)).catch(() => {});
    if (user?.is_super_admin || user?.permissions?.can_view_stats) {
      api.get('/admin/collaborazioni', { params: { stato: 'Da contattare' } }).then((r) => setCollabPending(r.data?.total || 0)).catch(() => {});
    }
  }, [navigate, user]);

  const logout = () => { clearAuth(); navigate('/admin/login'); };

  const can = (p) => user?.is_super_admin || !!user?.permissions?.[p];
  const isSuper = !!user?.is_super_admin;

  const NAV = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', testId: 'admin-nav-dashboard', end: true, show: true },
    { to: '/admin/locali', icon: Building2, label: 'Locali', testId: 'admin-nav-locali', show: can('can_manage_locali') },
    { to: '/admin/eventi', icon: CalendarDays, label: 'Eventi', testId: 'admin-nav-eventi', show: can('can_create_events') || can('can_publish_events') },
    { to: '/admin/revisione', icon: ClipboardList, label: 'Coda Revisione', testId: 'admin-nav-revisione', show: can('can_publish_events'), badge: pending },
    { to: '/admin/newsletter', icon: Mail, label: 'Newsletter', testId: 'admin-nav-newsletter', show: can('can_manage_newsletter') },
    { to: '/admin/collaborazioni', icon: Handshake, label: 'Collaborazioni', testId: 'admin-nav-collaborazioni', show: can('can_view_stats'), badge: collabPending },
    { to: '/admin/traffico', icon: TrendingUp, label: 'Statistiche Traffico', testId: 'admin-nav-traffico', show: can('can_view_stats') },
    { to: '/admin/team', icon: Users, label: 'Gestisci Team', testId: 'admin-nav-team', show: isSuper },
    { to: '/admin/settings', icon: SettingsIcon, label: 'Impostazioni', testId: 'admin-nav-settings', show: isSuper },
  ];

  const SideNav = () => (
    <div className="flex flex-col h-full">
      <Link to="/admin" className="flex items-center gap-2 px-4 h-14 border-b border-[color:var(--border-c)]">
        <Sparkles className="w-4 h-4 text-[color:var(--gold)]" />
        <span className="font-heading text-lg">Roma <span className="text-[color:var(--gold)]">Admin</span></span>
      </Link>
      {isSuper && (
        <div className="px-4 py-2 border-b border-[color:var(--border-c)] flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--gold)]">
          <ShieldAlert className="w-3 h-3"/>Super Admin
        </div>
      )}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.filter(n => n.show).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testId} className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? 'bg-[color:var(--surface)] text-[color:var(--gold)] border-l-2 border-[color:var(--gold-strong)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text)] hover:bg-[rgba(214,185,140,0.06)]'}`}>
            <item.icon className="w-4 h-4" />
            <span className="flex-1">{item.label}</span>
            {item.badge ? (<span className="text-xs px-2 py-0.5 rounded-full bg-[color:var(--gold-strong)] text-black font-semibold">{item.badge}</span>) : null}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[color:var(--border-c)] space-y-2">
        <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-[color:var(--text-subtle)] hover:text-[color:var(--gold)] px-3 py-2"><ExternalLink className="w-3 h-3"/> Vai al sito pubblico</Link>
        <div className="px-3 text-xs text-[color:var(--text-subtle)] truncate">{user?.nome ? `${user.nome} • ` : ''}{user?.email}</div>
        <Button variant="ghost" onClick={logout} data-testid="admin-logout-button" className="w-full justify-start text-[color:var(--text-muted)] hover:text-[color:var(--gold)]"><LogOut className="w-4 h-4 mr-2" /> Esci</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-[280px_1fr] bg-[color:var(--bg)] text-[color:var(--text)]" data-testid="admin-layout">
      <aside className="hidden lg:block border-r border-[color:var(--border-c)] bg-[color:var(--bg-2)]" data-testid="admin-sidebar">
        <SideNav />
      </aside>
      <div className="flex flex-col min-w-0">
        <header className="sticky top-0 z-30 border-b border-[color:var(--border-c)] bg-[rgba(16,17,19,0.92)] backdrop-blur">
          <div className="flex items-center gap-3 px-4 h-14">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-menu-toggle"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[280px] bg-[color:var(--bg-2)]">
                <SideNav />
              </SheetContent>
            </Sheet>
            <h1 className="font-heading text-lg md:text-xl flex-1" data-testid="admin-page-title">{title}</h1>
          </div>
        </header>
        <div className="p-4 md:p-6 max-w-7xl w-full">{children}</div>
      </div>
    </div>
  );
}
